/**
 * Narrative-hygiene backfill — lazy, render-time strip composer for
 * archived reports.
 *
 * **Why this module exists.**  The RNH-5, MEL, KFH-3, and WPG hygiene
 * passes run on the gen pipeline's exit ramp so newly generated reports
 * are clean at persist time. Reports generated BEFORE those passes
 * shipped still carry the original pollution in their persisted
 * narrative surfaces — verifier-tag markers (`[verified sed-section:…]`),
 * QA-review meta-instruction directives ("The analysis should be framed
 * around …"), unresolved template placeholders (`[Project Intent X]`),
 * and (for the drift flaggers) workload / capacity-vocabulary drift
 * relative to project intent.
 *
 * Re-running the gen-time pipeline against every archived doc would
 * either:
 *   (a) require a full corpus rewrite (back-pressure on Firestore, audit
 *       trail churn, version-number inflation), or
 *   (b) lock customers out of legacy reports until the backfill ran.
 *
 * Instead, the render side composes the strips on read. The persisted
 * report stays byte-identical at rest; the renderer just gets a cleaned
 * copy in memory.
 *
 * **What this module IS.**  A single composer that re-uses the gen-time
 * helpers verbatim (no fork, no parallel implementation). It runs in
 * the same order the gen pipeline does so the strips remain
 * order-stable:
 *   1. stripVerifierTags          (RNH-5)
 *   2. stripMetaInstructions      (MEL)
 *   3. stripUnresolvedPlaceholders (KFH-3)
 *   4. flagWorkloadProfileDrift   (WPG-2) — only when intent supplied
 *   5. flagCapacityDrift          (WPG-3) — only when intent supplied
 *
 * Steps 4+5 are pure ANNOTATORS — they stamp `qualityWarnings` without
 * touching the body. They are gated on `options.intent` so the
 * default render-time call (no intent context) doesn't double-stamp
 * drift warnings the gen pipeline already evaluated.
 *
 * **What this module IS NOT.**  This module MUST NOT write back to
 * Firestore. The strip is in-memory only; the archived doc stays as it
 * was. Reproducibility is paramount — a customer who exports a report
 * today must get the same artefact next month regardless of whether
 * the hygiene rules drift, AS LONG AS HYGIENE_VERSION is unchanged.
 * Bumping HYGIENE_VERSION (in narrativeHygiene.ts) is the signal that
 * a rule changed and re-strips on next read are expected.
 *
 * Pure / side-effect free apart from a single `console.info`
 * breadcrumb when at least one strip fired (gen-time helpers already
 * log per-rule). No async / no cancellation token / no Firestore I/O.
 */
import type { SiteRagReport } from '../types/siteRag';
import {
  stripVerifierTags,
  stripEmDashes,
  stripMetaInstructions,
  stripUnresolvedPlaceholders,
  flagWorkloadProfileDrift,
  flagCapacityDrift,
} from './narrativeHygiene';

/**
 * Discriminator strings for the per-strip applied list returned by
 * `applyHygieneBackfill`. Stable identifiers the renderer can use to
 * build the admin-only tooltip ("Cleaned at render — applied: …").
 *
 * The set intentionally MIRRORS the order of the strip-helper calls in
 * `applyHygieneBackfill` so the tooltip reads in pipeline order and the
 * stamped `qualityWarnings.applied` array is stable across reads with
 * the same input.
 */
export type BackfillFlag =
  | 'rnh-verifier-tags'
  | 'mel-meta-instructions'
  | 'kfh-unresolved-placeholders'
  | 'wpg-workload-drift'
  | 'wpg-capacity-drift';

/**
 * Optional intent context that unlocks the drift flaggers. When the
 * caller can't supply this, the two drift passes simply don't run —
 * the body-mutating strips still do.
 */
export interface BackfillOptions {
  /** Canonical workload-profile id from `effectiveWorkloadProfile(intent)`.
   *  Pass `undefined` to skip WPG-2's workload-drift flag. */
  workloadProfile?: string | null;
  /** Project-intent MW range, the same shape WPG-3 reads. Pass `null` to
   *  skip the capacity-drift flag (e.g. legacy / undecided intent). */
  capacityIntent?: { min: number; max: number } | null;
  /** Financial-assumptions capacity figure. Pass `undefined` to skip
   *  WPG-3 (no financial figure → no divergence possible). */
  financialCapacityMW?: number | null;
  /** Display label for the intent capacity, surfaced in the warning
   *  entry. Empty / undefined is fine — the renderer guards. */
  intentPowerLabel?: string | null;
}

export interface BackfillResult {
  /** The cleaned report. Identical to the input by reference ONLY when
   *  nothing fired. NB: the em-dash strip mutates the body WITHOUT
   *  contributing a `BackfillFlag`, so `report` can be a fresh cleaned
   *  copy even while `applied` is empty — always consume `report`; never
   *  infer "unchanged" from an empty `applied`. */
  report: SiteRagReport;
  /** Ordered list of FLAGGED strip rules that fired (changed the body
   *  OR stamped a new qualityWarning). The em-dash strip is intentionally
   *  unflagged (the persisted backfill-marker union is a fixed set), so
   *  an empty array does NOT imply the body was untouched — check
   *  `report` reference identity for that. */
  applied: BackfillFlag[];
}

/**
 * Detect whether a strip mutated the report. Identity short-circuit
 * upstream means a no-op strip returns the SAME object reference;
 * `next !== prev` is therefore the reliable + cheap signal that the
 * strip actually fired.
 *
 * Internal helper, kept local so the gen-time helpers don't need to
 * change their return shape just to support the lazy backfill.
 */
function didMutate(prev: SiteRagReport, next: SiteRagReport): boolean {
  return prev !== next;
}

/**
 * Lazy render-time backfill composer.
 *
 * Re-applies the gen-time hygiene strips in pipeline order. Returns
 * the cleaned report + the list of rules that actually changed
 * something. Body-mutating strips (verifier tags, em/en dashes,
 * meta-instructions, unresolved placeholders) always run; drift
 * annotators run only when the matching `options.*` field is supplied
 * so the default call (no intent context) doesn't re-stamp warnings the
 * gen pipeline already carried into the persisted doc. The em-dash strip
 * mutates the body but contributes no `BackfillFlag` (see `applied`).
 *
 * **Reproducibility contract.** This function MUST NOT write back to
 * Firestore. Callers receive a NEW in-memory report object on mutation
 * or the input by reference on identity. Persistence is the caller's
 * responsibility — but the design assumption (and the only sanctioned
 * code path) is render-only. See file-level docstring.
 *
 * **Identity short-circuit.** When no strip fired, the returned
 * `report` is `===` the input. Memoised selectors and React.memo
 * boundaries downstream stay hot.
 */
export function applyHygieneBackfill(
  report: SiteRagReport,
  options: BackfillOptions = {},
): BackfillResult {
  if (!report) return { report, applied: [] };

  const applied: BackfillFlag[] = [];
  let next: SiteRagReport = report;

  // 1. RNH-5 — verifier-tag strip.
  {
    const after = stripVerifierTags(next);
    if (didMutate(next, after)) applied.push('rnh-verifier-tags');
    next = after;
  }

  // 2. EMD — em/en-dash elimination. Sibling of the verifier-tag strip;
  // runs right after it so archived reports shed em/en dashes on render
  // the same way newly generated reports do at gen time. It mutates the
  // body but carries NO `BackfillFlag` of its own: the persisted
  // `qualityWarnings` backfill-marker (`siteRag.ts`) enumerates a FIXED
  // rule set, so a new identifier can't be added without widening that
  // persisted type. The reference-identity short-circuit at the tail
  // still returns the cleaned copy when this is the only strip that fired.
  next = stripEmDashes(next);

  // 3. MEL — meta-instruction strip.
  {
    const after = stripMetaInstructions(next);
    if (didMutate(next, after)) applied.push('mel-meta-instructions');
    next = after;
  }

  // 4. KFH-3 — unresolved template-placeholder strip.
  {
    const after = stripUnresolvedPlaceholders(next);
    if (didMutate(next, after)) applied.push('kfh-unresolved-placeholders');
    next = after;
  }

  // 5. WPG-2 — workload-profile drift annotator. Optional — gated on
  // the caller supplying a canonical workloadProfile id.
  if (options.workloadProfile != null && options.workloadProfile !== '') {
    const after = flagWorkloadProfileDrift(next, options.workloadProfile);
    if (didMutate(next, after)) applied.push('wpg-workload-drift');
    next = after;
  }

  // 6. WPG-3 — capacity-drift annotator. Optional — gated on the
  // caller supplying BOTH a non-null intent range AND a finite
  // financialCapacityMW (the flagger itself checks both, but a cheap
  // pre-check keeps this composer's intent obvious).
  if (
    options.capacityIntent != null
    && typeof options.financialCapacityMW === 'number'
    && Number.isFinite(options.financialCapacityMW)
  ) {
    const after = flagCapacityDrift(
      next,
      options.capacityIntent,
      options.financialCapacityMW,
      options.intentPowerLabel ?? '',
    );
    if (didMutate(next, after)) applied.push('wpg-capacity-drift');
    next = after;
  }

  if (next === report) {
    // True identity short-circuit — nothing fired (no flagged strip AND
    // no em-dash rewrite). Keyed on reference identity rather than
    // `applied.length` because the em-dash strip mutates the body
    // without contributing a flag.
    return { report, applied: [] };
  }

  // Single roll-up breadcrumb. Individual gen-time helpers log their
  // own per-rule lines; this one summarises the lazy backfill so an
  // ops grep for `[narrativeBackfill]` finds the read-side firings.
  // Guarded on `applied` so an em-dash-only read (body changed, no flag)
  // doesn't emit an empty `applied=` line.
  if (applied.length > 0) {
    console.info(
      `[narrativeBackfill] reportId=${report.id} applied=${applied.join(',')}`,
    );
  }

  return { report: next, applied };
}
